import React, { useId, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';

const backgroundColorDarkTheme = '#1a2035'; 
const fontColorDarkTheme = '#ffffff';
const borderColorDarkTheme = 'gray';
const disabledColorStr = 'rgb(96, 96, 96)';

interface DropdownV2Props<T extends object, V extends string | number> {
  label: string;
  options: T[];
  value?: V | null;
  onChange: (newValue: V | null) => void;
  labelField: keyof T;
  valueField: keyof T;
  isLoading?: boolean;
  errorMessage?: string;
  placeholder?: string;
  disabled?: boolean;
}

const DropdownV2Component = <T extends object, V extends string | number>({
  label,
  options = [],
  value,
  onChange,
  labelField,
  valueField,
  isLoading = false,
  errorMessage,
  placeholder = 'Selecione uma opção',
  disabled = false,
}: DropdownV2Props<T, V>) => {
  const id = useId();
  const labelId = `${id}-label`;

  const getOptionLabel = useCallback(
    (option: T): string => {
      const val = option[labelField];
      return val === null || val === undefined ? '' : String(val);
    },
    [labelField]
  );

  return (
    <FormControl 
      fullWidth 
      error={!!errorMessage} 
      disabled={disabled || isLoading} 
      sx={{ mb: 3 }}
    >
      <InputLabel 
        id={labelId} 
        sx={{ 
          color: fontColorDarkTheme, 
          '&.Mui-focused': { color: fontColorDarkTheme },
          '&.Mui-error': { color: 'error.main' },
          '&.Mui-error.Mui-focused': { color: 'error.main' },
          '&.Mui-disabled': { color: disabledColorStr },
        }}
      >
        {label}
      </InputLabel>
      
      <Select
        labelId={labelId}
        id={`${id}-select`}
        value={value === null || value === undefined ? '' : value} 
        label={label}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === '' ? null : (val as V));
        }}
        
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: backgroundColorDarkTheme,
              border: `1px solid ${borderColorDarkTheme}`,
              color: fontColorDarkTheme,
              maxHeight: 250,
            },
          },
        }}
        sx={{
          color: fontColorDarkTheme,
          '& .MuiSvgIcon-root': { 
            color: disabled ? disabledColorStr : fontColorDarkTheme 
          },
          
          '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColorDarkTheme },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: fontColorDarkTheme },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
          
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
          '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },

          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { 
            borderColor: disabledColorStr 
          },
          '&.Mui-disabled .MuiSelect-select': {
            color: disabledColorStr,
            WebkitTextFillColor: disabledColorStr,
          },
        }}
      >

        <MenuItem value="" disabled>
          <em>{isLoading ? 'Carregando...' : placeholder}</em>
        </MenuItem>

        {isLoading && options.length === 0 && (
          <MenuItem disabled value="" sx={{ justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} color="inherit" />
          </MenuItem>
        )}

        {!isLoading && options.length === 0 && (
          <MenuItem disabled value="">
            <em>Nenhuma opção disponível</em>
          </MenuItem>
        )}

        {options.map((option) => (
          <MenuItem
            key={String(option[valueField])}
            value={option[valueField] as string | number}
            sx={{
              backgroundColor: backgroundColorDarkTheme,
              color: fontColorDarkTheme,
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: '#2a2a2a',
              },
              '&.Mui-selected': {
                backgroundColor: '#3a3a3a !important',
              },
              '&.Mui-selected:hover': {
                backgroundColor: '#4a4a4a !important',
              },
            }}
          >
            {getOptionLabel(option)}
          </MenuItem>
        ))}
      </Select>
      {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default DropdownV2Component;